// Email HTML primitives. Everything here follows the rules that hold across
// Gmail, Apple Mail, Outlook (classic and new), Yahoo and Samsung in 2026:
// tables for structure, inline styles, widths as HTML attributes, fluid
// hybrid columns that stack without media queries, and bulletproof buttons.
import { fontStack, tone as toneOf } from './theme'

export const W = 600 // email width

// ─── Text ───
export const esc = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

// Links keep merge tags ({{BookingLink}}) as-is; anything else must look like
// a URL, mailto: or tel:, so a stray "javascript:" can never ship.
export const safeUrl = (u) => {
  const s = String(u || '').trim()
  if (!s) return '#'
  if (/^\{\{\s*\w+\s*\}\}$/.test(s)) return s
  if (/^(https?:|mailto:|tel:)/i.test(s)) return esc(s)
  if (/^www\./i.test(s)) return esc(`https://${s}`)
  if (/^#/.test(s)) return esc(s)
  return '#'
}

// Inline formatting for body copy: **bold**, *italic*, [label](url).
// Escapes first, so salon-typed text can never inject markup.
export const inline = (text, { linkColor } = {}) => {
  let s = esc(text)
  s = s.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (m, label, url) => `<a href="${safeUrl(url.replace(/&amp;/g, '&'))}" style="color:${linkColor || 'inherit'};text-decoration:underline;">${label}</a>`)
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  s = s.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>')
  return s
}
// Paragraphs split on blank lines; single newlines become <br>.
export const paragraphs = (text, style, opts = {}) => {
  const parts = String(text || '').split(/\n\s*\n/).map(p => p.trim()).filter(Boolean)
  return parts.map((p, i) => `<p style="margin:0 0 ${i === parts.length - 1 ? 0 : opts.gap || 14}px 0;${style}">${inline(p, opts).replace(/\n/g, '<br>')}</p>`).join('')
}

// ─── Type scale (desktop px; mobile sizes live in the <style> block) ───
export const SIZES = {
  xxl: { size: 72, lh: 0.98, cls: 'es-dxxl' },
  xl: { size: 54, lh: 1.02, cls: 'es-dxl' },
  l: { size: 42, lh: 1.06, cls: 'es-dl' },
  m: { size: 32, lh: 1.12, cls: 'es-dm' },
  s: { size: 24, lh: 1.2, cls: 'es-ds' },
  xs: { size: 19, lh: 1.3, cls: 'es-dxs' },
}

export const displayStyle = (theme, size = 'm', color) => {
  const t = theme.type
  const sz = SIZES[size] || SIZES.m
  // Condensed caps read smaller, so they get a little more size
  const bump = t.displayCase === 'upper' ? 1.06 : 1
  return `font-family:${fontStack(t.display)};font-size:${Math.round(sz.size * bump)}px;line-height:${sz.lh};font-weight:${t.displayWeight};letter-spacing:${t.displayTracking};${t.displayCase === 'upper' ? 'text-transform:uppercase;' : ''}color:${color};text-wrap:balance;mso-line-height-rule:exactly;`
}
// The italic accent: the display face's own italic, or, for looks that pair
// a heavy sans with a serif (Zine), the serif's italic at a normal weight.
export const italicAttrs = (theme) => {
  const f = theme.type.italicFont
  return f
    ? ` class="es-f-italic" style="font-family:${fontStack(f)};font-style:italic;font-weight:400;letter-spacing:-0.01em;"`
    : ' style="font-style:italic;"'
}
// Headlines may carry *one italic accent* ("The *spring* edit"): an editorial
// flourish for looks that support it, plain emphasis for those that don't.
export const headline = (text, theme, { size = 'm', color, align = 'center', tag = 'h1', margin = '0' } = {}) => {
  const sz = SIZES[size] || SIZES.m
  let body = esc(text).replace(/\n/g, '<br>')
  body = body.replace(/\*([^*]+)\*/g, theme.type.italicAccent ? `<em${italicAttrs(theme)}>$1</em>` : '$1')
  return `<${tag} class="${sz.cls} es-f-display" style="margin:${margin};${displayStyle(theme, size, color)}text-align:${align};">${body}</${tag}>`
}
export const eyebrow = (text, theme, { color, align = 'center', margin = '0 0 14px 0' } = {}) => (
  text ? `<p class="es-f-text" style="margin:${margin};font-family:${fontStack(theme.type.text)};font-size:11px;line-height:1.4;font-weight:700;letter-spacing:0.18em;text-transform:uppercase;color:${color};text-align:${align};mso-line-height-rule:exactly;">${esc(text)}</p>` : ''
)
export const bodyStyle = (theme, color, size = 16) => `font-family:${fontStack(theme.type.text)};font-size:${size}px;line-height:1.6;color:${color};mso-line-height-rule:exactly;`
export const body = (text, theme, { color, align = 'center', size = 16, linkColor, margin = '0' } = {}) => (
  text ? `<div class="es-f-text" style="margin:${margin};text-align:${align};">${paragraphs(text, bodyStyle(theme, color, size), { linkColor: linkColor || color })}</div>` : ''
)
export const small = (text, theme, { color, align = 'center', margin = '0' } = {}) => (
  text ? `<p class="es-f-text" style="margin:${margin};font-family:${fontStack(theme.type.text)};font-size:12px;line-height:1.55;color:${color};text-align:${align};">${inline(text, { linkColor: color })}</p>` : ''
)

// ─── Buttons ───
// Bulletproof: the padding sits on the <a>, so the whole shape is tappable
// everywhere; MSO gets its padding from the <i> spacers (Outlook ignores
// padding on inline elements). Radius is lost in classic Outlook, which is fine.
export const button = (text, url, theme, t, { style, align = 'center', full = false, margin = '0' } = {}) => {
  if (!text) return ''
  const b = theme.button
  const kind = style || b.style
  const radius = b.shape === 'pill' ? 999 : b.shape === 'rounded' ? 10 : 2
  const upper = b.case === 'upper'
  const fs = upper ? 13 : 15
  const ls = upper ? '0.12em' : '0.01em'
  if (kind === 'link') {
    return `<p style="margin:${margin};text-align:${align};"><a href="${safeUrl(url)}" class="es-f-text" style="font-family:${fontStack(theme.type.text)};font-size:15px;font-weight:600;color:${t.accentText};text-decoration:underline;text-underline-offset:3px;">${esc(text)}&nbsp;&rarr;</a></p>`
  }
  const outline = kind === 'outline'
  const bg = outline ? 'transparent' : t.btnBg
  const fg = outline ? (t.id === 'accent' ? t.text : t.accentText) : t.btnText
  const border = outline ? (t.id === 'accent' ? t.text : t.accentText) : t.btnBg
  const width = full ? 'width:100%;' : ''
  const tableAttrs = full ? 'width="100%"' : ''
  return `<table role="presentation" ${tableAttrs} class="es-btnt" cellpadding="0" cellspacing="0" border="0" ${align === 'left' ? '' : `align="${align === 'right' ? 'right' : 'center'}"`} style="margin:${margin};${align === 'center' ? 'margin-left:auto;margin-right:auto;' : ''}border-collapse:separate;"><tr><td align="center" ${outline ? '' : `bgcolor="${bg}"`} style="border-radius:${radius}px;${outline ? '' : `background-color:${bg};`}"><a href="${safeUrl(url)}" class="es-btn es-f-text" style="display:inline-block;${width}box-sizing:border-box;padding:15px 30px;border:1.5px solid ${border};border-radius:${radius}px;font-family:${fontStack(theme.type.text)};font-size:${fs}px;line-height:1.2;font-weight:700;letter-spacing:${ls};${upper ? 'text-transform:uppercase;' : ''}color:${fg};text-decoration:none;text-align:center;mso-padding-alt:0;"><!--[if mso]><i style="letter-spacing:30px;mso-font-width:-100%;mso-text-raise:28pt;">&nbsp;</i><![endif]--><span style="mso-text-raise:14pt;">${esc(text)}</span><!--[if mso]><i style="letter-spacing:30px;mso-font-width:-100%;">&nbsp;</i><![endif]--></a></td></tr></table>`
}

// ─── Images ───
// Unsplash images get sized for email: 2x the displayed width, JPEG (Outlook
// can't show WebP), cropped to the ratio the layout wants. A template photo
// can carry a focal point (fp-x, fp-y, fp-z) to steer the crop.
export const sizedSrc = (src, w, ratio) => {
  const s = String(src || '')
  if (/^https:\/\/images\.unsplash\.com\//.test(s)) {
    const [base, query = ''] = s.split('?')
    const focal = query.split('&').filter(p => /^fp-(x|y|z)=[\d.]+$/.test(p))
    const crop = focal.length ? `crop=focalpoint&${focal.join('&')}` : 'crop=entropy'
    const h = ratio ? Math.round((w * 2) / ratio) : null
    return `${base}?w=${w * 2}${h ? `&h=${h}&fit=crop&${crop}` : ''}&fm=jpg&q=72`
  }
  return s
}
export const image = (src, { width = W, alt = '', radius = 0, href, ratio, cls = '', block = true } = {}) => {
  if (!src) return ''
  const img = `<img src="${esc(sizedSrc(src, width, ratio))}" width="${width}" alt="${esc(alt)}" class="es-img ${cls}" style="display:${block ? 'block' : 'inline-block'};width:100%;max-width:${width}px;height:auto;border:0;outline:none;text-decoration:none;${radius ? `border-radius:${radius}px;` : ''}">`
  return href ? `<a href="${safeUrl(href)}" style="text-decoration:none;">${img}</a>` : img
}
// A placeholder while the salon hasn't picked an image. Builder only (the
// sent email simply leaves it out), so it can use an inline SVG icon.
export const imagePlaceholder = (width, ratio, t, label = 'Add an image') => {
  const h = Math.min(Math.round(width / (ratio || 1.5)), 420)
  const icon = `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="${t.muted}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" style="display:block;margin:0 auto 8px auto;opacity:0.8;"><rect x="3" y="4" width="18" height="16" rx="2.5"/><circle cx="8.5" cy="9.5" r="1.6"/><path d="M21 16l-5-5-9 9"/></svg>`
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" class="es-ph"><tr><td height="${h}" align="center" valign="middle" style="height:${h}px;background-color:${t.line};font-family:Arial,sans-serif;font-size:13px;font-weight:600;color:${t.muted};text-align:center;">${icon}${esc(label)}</td></tr></table>`
}

// ─── Layout ───
// One full-width section with a tone background.
export const PAD = { s: 28, m: 44, l: 60, none: 0 }
export const section = (inner, t, { py = 'm', px = 40, cls = '', ptop, pbottom, bg } = {}) => {
  const top = ptop != null ? ptop : PAD[py] != null ? PAD[py] : PAD.m
  const bottom = pbottom != null ? pbottom : top
  const background = bg || t.bg
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:${background};"><tr><td class="es-t-${t.id} ${px ? 'es-px' : ''} ${cls}" style="padding:${top}px ${px}px ${bottom}px ${px}px;background-color:${background};">${inner}</td></tr></table>`
}

// Fluid hybrid columns: inline-block divs that sit side by side when there's
// room and wrap on phones (no media query needed, so Gmail's app stacks them
// too), with an MSO ghost table so classic Outlook keeps them in a row.
// Each column's max-width includes its share of the gap as inner padding, so
// a stacked column is exactly the container's width (no overflow on phones).
export const columns = (cells, { inner = W - 80, gap = 20, valign = 'top', widths } = {}) => {
  const n = cells.length
  const ws = widths || cells.map((c, i) => Math.floor(inner / n) + (i === n - 1 ? inner - Math.floor(inner / n) * n : 0))
  const half = Math.round(gap / 2)
  const pad = (i) => `0 ${i === n - 1 ? 0 : half}px 0 ${i === 0 ? 0 : half}px`
  const mso = (i) => `<!--[if mso]>${i === 0 ? `<table role="presentation" width="${inner}" cellpadding="0" cellspacing="0" border="0"><tr>` : '</td>'}<td width="${ws[i]}" valign="${valign}"><![endif]-->`
  const divs = cells.map((c, i) => `${mso(i)}<div class="es-col" style="display:inline-block;vertical-align:${valign};width:100%;max-width:${ws[i]}px;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td class="es-colpad" style="padding:${pad(i)};vertical-align:${valign};text-align:left;font-size:16px;line-height:1.5;">${c}</td></tr></table></div>`).join('')
  return `<div class="es-cols" style="font-size:0;line-height:0;text-align:center;">${divs}<!--[if mso]></td></tr></table><![endif]--></div>`
}

export const rule = (color, { width = '100%', margin = '0', align = 'center', thickness = 1 } = {}) => (
  `<table role="presentation" width="${width === '100%' ? '100%' : width}" cellpadding="0" cellspacing="0" border="0" align="${align}" style="margin:${margin};${align === 'center' ? 'margin-left:auto;margin-right:auto;' : ''}"><tr><td height="${thickness}" style="height:${thickness}px;line-height:${thickness}px;font-size:1px;background-color:${color};">&nbsp;</td></tr></table>`
)
export const spacer = (h) => `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td height="${h}" style="height:${h}px;line-height:${h}px;font-size:1px;">&nbsp;</td></tr></table>`

export const toneFor = (theme, id) => toneOf(theme, id)
